import React from "react";

import {
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_HREF,
  SUPPORT_NUMBER,
  SUPPORT_NUMBER_HREF,
} from "@/constants/aptihealth";
import { AcknowledgedPolicyEnum } from "@/core/constants";
import { Button2 } from "@/ui2/atoms/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Link2 } from "@/ui2/atoms/Link";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { trackEvent } from "@/utils/EventTrackingUtil";

type InsurancePolicy =
  | typeof AcknowledgedPolicyEnum.OUT_OF_NETWORK_INSURANCE_POLICY_NAME
  | typeof AcknowledgedPolicyEnum.UNINSURED_POLICY_NAME;

type PolicyProps = {
  policyName: InsurancePolicy;
  submitHandler: () => Promise<void>;
};

export function InsurancePolicy({ policyName, submitHandler }: PolicyProps) {
  return (
    <Card2 className="tw:mx-auto tw:flex-col tw:gap-8 tw:p-12 tw:text-center tw:lg:max-w-148">
      <Heading2 as="h1" className="tw:text-center" size={4}>
        Your Insurance Information Was Recently Changed
      </Heading2>

      <p className="body-md" {...getAutomationDataAttr("insurance-policy-subtitle")}>
        {renderInsurancePolicy(policyName)}
      </p>

      <Button2
        className="tw:min-w-64 tw:self-center"
        onPress={submitHandler}
        {...getAutomationDataAttr("insurance-policy-acknowledge-button")}
      >
        Acknowledge
      </Button2>

      <p className="body-md">
        If you need assistance or have any questions please contact{" "}
        <Link2 href={SUPPORT_EMAIL_HREF}>{SUPPORT_EMAIL}</Link2> or{" "}
        <Link2 href={SUPPORT_NUMBER_HREF}>{SUPPORT_NUMBER}</Link2>
      </p>
    </Card2>
  );
}

function renderInsurancePolicy(policyName: InsurancePolicy) {
  switch (policyName) {
    case AcknowledgedPolicyEnum.OUT_OF_NETWORK_INSURANCE_POLICY_NAME:
      return (
        <>
          The insurance company may be out-of-network. You will be responsible for any fees
          associated with your care. Learn about&nbsp;
          <Link2
            href="/session-pricing"
            isExternal
            onClick={() => trackEvent("patient-chart-insurance-not-listed-modal-click-rates")}
          >
            aptihealth's rates
          </Link2>
        </>
      );

    case AcknowledgedPolicyEnum.UNINSURED_POLICY_NAME:
      return (
        <>
          You have no insurance on file. You will be responsible for any fees associated with your
          care. Learn about&nbsp;
          <Link2
            href="/session-pricing"
            isExternal
            onClick={() => trackEvent("patient-chart-no-insurance-modal-click-rates")}
          >
            aptihealth's rates
          </Link2>
        </>
      );

    default:
      return null;
  }
}
